import { type Dispatch, type SetStateAction, useMemo, useState, type MouseEvent, JSX } from "react";

import { Box, Theme, useTheme } from "@mui/material";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import getSeriesColor from "src/lib/color.helper";
import { type MeasuresSummarizedItemDto, type MeasuresSummarizedDto } from "src/types/dashboardDatasetDto";
import { type MeasuresItemizedDto } from "src/types/locationChartDto";
import LocationType from "src/types/LocationType";

import CustomPagination from "./CustomPagination";
import createColumns from "./TableColumns";
import ErrorAlert from "../ErrorAlert/ErrorAlert";

const defaultRows = [6];
const styles = {
  grid: {
    border: 0,
  },
  gridBox: {
    width: "100%",
  },
};

const customSlotProps = {
  pagination: {
    component: CustomPagination,
  },
};

function getCustomSlotProps(rowSlotProps: {
  onMouseEnter: (params: MouseEvent<HTMLDivElement>) => void;
  onMouseLeave: (params: MouseEvent<HTMLDivElement>) => void;
  onClick: (params: MouseEvent<HTMLDivElement>) => void;
}): {
  pagination: {
    component: () => JSX.Element;
  };
  row: {
    onMouseEnter: (params: MouseEvent<HTMLDivElement>) => void;
    onMouseLeave: (params: MouseEvent<HTMLDivElement>) => void;
    onClick: (params: MouseEvent<HTMLDivElement>) => void;
  };
} {
  return {
    ...customSlotProps,
    row: rowSlotProps,
  };
}

function getColumns(
  showColorColumn: boolean,
  showActions: boolean,
  theme: Theme,
  setError: Dispatch<SetStateAction<string | null>>
): GridColDef[] {
  const columns = createColumns(showColorColumn, theme, setError);
  return showActions ? columns : columns.filter((col) => col.field !== "actions");
}

function TableView({
  measuresData,
  displayedType,
  rows = defaultRows,
  hideFooter = false,
  showSeriesColors = false,
  showActions = false,
}: {
  measuresData: MeasuresSummarizedDto | MeasuresItemizedDto | null | undefined;
  displayedType: LocationType;
  rows: number[];
  hideFooter?: boolean;
  showSeriesColors?: boolean;
  showActions?: boolean;
}): JSX.Element | null {
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const navigate = useNavigate();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: rows[0] });
  const data = measuresData?.data as unknown as MeasuresSummarizedItemDto[];

  const tableRows = useMemo(
    () =>
      data?.map((row, index) => ({
        id: row.locationId || index,
        color: getSeriesColor(index, theme),
        localName: row.localName,
        currentValue: row.currentValue,
        pastValue: row.pastValue,
        difference: row,
        differenceChip: row,
      })),
    [data, theme]
  );

  const dynamicColumns = useMemo(() => getColumns(showSeriesColors, showActions, theme, setError), [showActions, showSeriesColors, theme]);

  if (!data) return null;

  const rowSlotProps = {
    onMouseEnter: (params: MouseEvent<HTMLDivElement>) => {
      params.stopPropagation();
    },
    onMouseLeave: (params: MouseEvent<HTMLDivElement>) => {
      params.stopPropagation();
    },
    onClick: (params: MouseEvent<HTMLDivElement>) => {
      if (showActions && !isMobile) {
        if (displayedType === LocationType.Mall) {
          navigate(`/center-dashboard/${params.currentTarget.dataset.id}`);
        } else if (displayedType === LocationType.Shop) {
          navigate(`/shop-dashboard/${params.currentTarget.dataset.id}`);
        } else if (displayedType === LocationType.Station) {
          navigate(`/station-dashboard/${params.currentTarget.dataset.id}`);
        }
      }
    },
  };

  return (
    <>
      <ErrorAlert error={error} setError={setError} />
      <Box sx={styles.gridBox}>
        <DataGrid
          disableColumnMenu
          rows={tableRows}
          columns={dynamicColumns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={rows}
          sx={styles.grid}
          rowHeight={47}
          columnHeaderHeight={40}
          hideFooter={hideFooter}
          hideFooterSelectedRowCount
          scrollbarSize={0}
          slotProps={getCustomSlotProps(rowSlotProps)}
        />
      </Box>
    </>
  );
}

TableView.defaultProps = {
  hideFooter: false,
  showSeriesColors: false,
  showActions: false,
};

export default TableView;
